import * as React from 'react'
import {
  AssignChartDiscItem,
  AssignChartScanPayload,
  ChartScanDetail,
} from '@/lib/api/company/chartScan'
import { useQueryDrivers } from '@/lib/api/company/drivers/useQueryDrivers'
import { useQueryVehiclesList } from '@/lib/api/company/vehicle-list/useQueryVehiclesList'
import { TableProvider, useTable } from '@/modules/drivers/context/TableContext'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableCheckbox from '@/modules/drivers/shared/tableItems/TableCheckbox'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { ConfirmModal } from '@/modules/drivers/modals/ConfirmModal'
import { DateField } from '@/ui/form/controls/DateField'
import { FaIcon, faTrash } from '@/lib/fa'
import { SearchSelect } from './editor/SearchSelect'
import { fmt, useUnderscaleFactor, pluralKey, useChartT } from './i18n'

// 1:1 with WPF AssignLoadedImageToDriverView. WPF's DataGrid has one row per
// loaded image file with per-row Date / Driver / Vehicle / DontReadActivities,
// plus a top-bar bulk-apply that copies the toolbar values into every selected
// row. The web dialog takes the same multi-file selection, but each file becomes
// its own scan with N detected discs, so we get one row per detected disc (with a
// "Plik" column once more than one file is loaded) instead of one row per file.
// Otherwise identical shape and bulk-apply semantics.

type Props = {
  companyId: string
  scans: ChartScanDetail[]
  isSaving: boolean
  onCancel: () => void
  onContinue: (assignments: ScanAssignment[]) => void

  onDiscardDisc?: (scanId: number, discId: number) => Promise<void>
  continueLabel?: string
  warning?: string | null
}

// One assign call per scan (= per file picked in the dialog).
export type ScanAssignment = {
  scanId: number
  payload: AssignChartScanPayload
}

type RowState = {
  scanId: number
  fileName: string
  discId: number
  chartDate: string
  driverId: number | null
  vehicleId: number | null
  dontReadActivities: boolean
}

function todayIso(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function dateOnly(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : ''
}

// Three-state checkbox mirroring WPF's `<CheckBox IsThreeState="True">` used in
// the bulk-apply toolbar (AssignLoadedImageToDriverView.xaml:82). HTML checkboxes
// only expose `indeterminate` via the DOM property, so we set it through a ref.
// Click cycle is 1:1 with WPF ToggleButton.OnToggle: true → null, false → true,
// null → false. null = "leave each row's current value alone on Apply".
function ThreeStateCheckbox({
  value,
  onChange,
}: {
  value: boolean | null
  onChange: (next: boolean | null) => void
}) {
  const ref = React.useRef<HTMLInputElement>(null)
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = value === null
  }, [value])
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={value === true}
      onChange={() => {
        if (value === true) onChange(null)
        else if (value === false) onChange(true)
        else onChange(false)
      }}
    />
  )
}

function useDriverSearch(companyId: string) {
  // Nazwany `use*` celowo: to jest hook - dziecko woła go w swoim renderze przez props
  // `useSearch`. Anonimowa strzałka wyglądała dla lintera na zwykłą funkcję z hookiem w środku.
  return function useSearch(search: string) {
    const q = useQueryDrivers({ companyId, search, pageSize: 25, isActive: true })
    const list = (q.data as any) || []
    const drivers = Array.isArray(list) ? list : list.drivers || []
    return {
      items: drivers.map((d: any) => ({
        id: d.id,
        label: d.fullName || `${d.surname} ${d.name}`,
      })),
      isLoading: q.isLoading,
    }
  }
}

function useVehicleSearch(companyId: string) {
  return function useSearch(search: string) {
    const q = useQueryVehiclesList({ companyId, search, pageSize: 25, isActive: true })
    const list = (q.data as any) || []
    const vehicles = Array.isArray(list) ? list : list.vehicles || []
    return {
      items: vehicles.map((v: any) => ({
        id: v.id,
        label: v.registrationNumber || v.registrationNumberFromTachograph || `#${v.id}`,
      })),
      isLoading: q.isLoading,
    }
  }
}

// Polish needs three plural forms for "tarczka"; the resource files carry the
// same one/few/many triple for every language (two-form languages simply repeat
// the plural in `Few`/`Many`).
const DETECTED_DEFAULTS: Record<'One' | 'Few' | 'Many', string> = {
  One: 'Wykryto {0} tarczkę',
  Few: 'Wykryto {0} tarczki',
  Many: 'Wykryto {0} tarczek',
}

const StepAssignInner: React.FC<Props> = ({
  companyId,
  scans,
  isSaving,
  onCancel,
  onContinue,
  onDiscardDisc,
  continueLabel,
  warning,
}) => {
  const T = useChartT()
  const driverSearch = useDriverSearch(companyId)
  const vehicleSearch = useVehicleSearch(companyId)

  const isMultiFile = scans.length > 1
  // How many times too small the discs are — the backend reports the enlargement at
  // which the same scan starts to read.
  const underscaleFactor = useUnderscaleFactor(scans)
  const scanIdsKey = scans.map((s) => s.scanId).join(',')

  const initialRows = React.useMemo<RowState[]>(
    () =>
      scans.flatMap((scan) =>
        scan.discs.map((d) => ({
          scanId: scan.scanId,
          fileName: scan.fileName,
          discId: d.discId,
          chartDate: dateOnly(d.dayStartDate) || scan.chartDate?.slice(0, 10) || todayIso(),
          driverId: d.driverId ?? scan.driverId ?? null,
          vehicleId: d.vehicleId ?? scan.vehicleId ?? null,
          dontReadActivities: scan.dontReadActivities ?? false,
        })),
      ),
    // Only seed once per set of scans — manual edits below shouldn't get clobbered by re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scanIdsKey],
  )
  const [rows, setRows] = React.useState<RowState[]>(initialRows)

  // Re-seed if the loaded scans change.
  React.useEffect(() => {
    setRows(initialRows)
  }, [initialRows])

  // Bulk-apply toolbar state (mirrors WPF's top-bar fields). null = "leave row's
  // current value alone when Apply is clicked".
  const [bulkDate, setBulkDate] = React.useState<string>('')
  const [bulkDriverId, setBulkDriverId] = React.useState<number | null>(null)
  const [bulkVehicleId, setBulkVehicleId] = React.useState<number | null>(null)
  // null = "leave alone" (indeterminate), matching WPF DontReadActivitiesForSelected (bool?).
  const [bulkDontRead, setBulkDontRead] = React.useState<boolean | null>(null)

  const { toggleAll, toggleId, selectedGroup, isSelectedAny, setAllIds, resetAll } = useTable()
  const selectedIds = (selectedGroup?.ids ?? []) as number[]

  React.useEffect(() => {
    setAllIds(rows.map((r) => ({ id: r.discId })))
  }, [rows, setAllIds])

  const updateRow = (discId: number, patch: Partial<RowState>) => {
    setRows((prev) => prev.map((r) => (r.discId === discId ? { ...r, ...patch } : r)))
  }

  const applyBulk = () => {
    if (selectedIds.length === 0) return
    setRows((prev) =>
      prev.map((r) => {
        if (!selectedIds.includes(r.discId)) return r
        return {
          ...r,
          chartDate: bulkDate || r.chartDate,
          driverId: bulkDriverId !== null ? bulkDriverId : r.driverId,
          vehicleId: bulkVehicleId !== null ? bulkVehicleId : r.vehicleId,
          dontReadActivities:
            bulkDontRead !== null ? bulkDontRead : r.dontReadActivities,
        }
      }),
    )
    resetAll()
  }

  // The server binds chartDate to a non-nullable DateTime, so an empty day is a
  // hard 400 at model binding (not a handler-level validation message). Clearing
  // the DateField sets the row to '', so block the step instead of posting it.
  const rowsMissingDate = rows.filter((r) => !r.chartDate).length

  const [discardingId, setDiscardingId] = React.useState<number | null>(null)
  const [discardError, setDiscardError] = React.useState<string | null>(null)

  const [pendingDiscard, setPendingDiscard] = React.useState<
    { row: RowState; discNo: number } | null
  >(null)

  const handleDiscard = async (row: RowState) => {
    if (!onDiscardDisc || discardingId !== null) return
    setDiscardError(null)
    setDiscardingId(row.discId)
    try {
      await onDiscardDisc(row.scanId, row.discId)

      if (selectedIds.includes(row.discId)) toggleId(row.discId)
      setRows((prev) => prev.filter((r) => r.discId !== row.discId))
    } catch {
      setDiscardError(T('discardFailed', 'Nie udało się odrzucić tarczki. Spróbuj ponownie.'))
    } finally {
      setDiscardingId(null)
    }
  }

  const discardedCount = initialRows.length - rows.length

  const handleContinue = () => {
    if (rowsMissingDate > 0) return
    const assignments = scans
      .filter((scan) => rows.some((r) => r.scanId === scan.scanId))
      .map<ScanAssignment>((scan) => ({
        scanId: scan.scanId,
        payload: {
          discs: rows
            .filter((r) => r.scanId === scan.scanId)
            .map<AssignChartDiscItem>((r) => ({
              discId: r.discId,
              chartDate: r.chartDate,
              driverId: r.driverId,
              vehicleId: r.vehicleId,
              dontReadActivities: r.dontReadActivities,
            })),
        },
      }))
    onContinue(assignments)
  }

  const columns = React.useMemo<TableHeaderColumn[]>(
    () => [
      ...(isMultiFile
        ? ([
            { title: T('colFile', 'Plik'), key: 'file', type: 'string', sort: false },
          ] as TableHeaderColumn[])
        : []),
      { title: T('colDisc', 'Tarczka'), key: 'disc', type: 'string', sort: false },
      { title: T('colDay', 'Dzień'), key: 'date', type: 'string', sort: false },
      { title: T('colDriver', 'Kierowca'), key: 'driver', type: 'string', sort: false },
      { title: T('colVehicle', 'Pojazd'), key: 'vehicle', type: 'string', sort: false },
      {
        title: T('dontReadActivities', 'Nie czytaj aktywności'),
        key: 'dontRead',
        type: 'string',
        sort: false,
      },
      ...(onDiscardDisc
        ? ([
            { title: T('listActions', 'Akcje'), key: 'actions', type: 'string', sort: false },
          ] as TableHeaderColumn[])
        : []),
    ],
    [isMultiFile, onDiscardDisc, T],
  )

  return (
    <div className="tw-flex tw-max-h-[82vh] tw-flex-col">
      <header className="tw-border-b tw-border-base-300 tw-px-5 tw-py-3">
        <h2 className="tw-text-base tw-font-semibold tw-text-content-base">
          {T('assignTitle', 'Przypisz dane do tarczek')}
        </h2>
        <p className="tw-text-xs tw-text-neutral">
          {fmt(
            T(`detectedDiscs${pluralKey(rows.length)}`, DETECTED_DEFAULTS[pluralKey(rows.length)]),
            rows.length,
          )}{' '}
          {isMultiFile ? (
            <>{fmt(T('inFiles', 'w {0} plikach'), scans.length)}</>
          ) : (
            <>
              {T('inFile', 'w pliku')}{' '}
              <span className="tw-font-mono">{scans[0]?.fileName}</span>
            </>
          )}
        </p>
        {discardedCount > 0 && (
          <p className="tw-mt-1 tw-text-xs tw-text-neutral">
            {fmt(
              T('discardedCount', 'Odrzucono {0} z {1} wykrytych tarczek.'),
              discardedCount,
              initialRows.length,
            )}
          </p>
        )}
        {warning && <p className="tw-mt-1 tw-text-xs tw-text-amber-700">{warning}</p>}
        {underscaleFactor && (
          <p className="tw-mt-1 tw-max-w-3xl tw-text-xs tw-text-amber-700">
            {fmt(
              T(
                'lowResolutionScan',
                'Skan ma zbyt niską rozdzielczość. Tarczki są na nim około {0}× za małe.'
                  + ' Zeskanuj w 300 dpi (tarczka powinna mieć około 1450 px średnicy).',
              ),
              underscaleFactor,
            )}
          </p>
        )}
      </header>

      <div className="tw-mt-4 tw-mx-5 tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-shadow-sm">
        {/* Bulk-apply action bar — mirrors WPF's top-of-grid toolbar. Values
            here get copied into selected rows when "Zastosuj" is clicked. */}
        <div className="tw-flex tw-w-full tw-flex-shrink-0 tw-flex-wrap tw-items-end tw-gap-3 tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-px-4 tw-py-3">
          <div className="tw-w-44">
            <DateField
              label={T('colDay', 'Dzień')}
              value={bulkDate}
              setValue={(v) => setBulkDate(v ?? '')}
            />
          </div>
          <div className="tw-w-56">
            <SearchSelect
              label={T('colDriver', 'Kierowca')}
              placeholder={T('search', 'Wyszukaj...')}
              value={bulkDriverId}
              onChange={setBulkDriverId}
              useSearch={driverSearch}
            />
          </div>
          <div className="tw-w-56">
            <SearchSelect
              label={T('colVehicle', 'Pojazd')}
              placeholder={T('search', 'Wyszukaj...')}
              value={bulkVehicleId}
              onChange={setBulkVehicleId}
              useSearch={vehicleSearch}
            />
          </div>
          <label className="tw-flex tw-items-center tw-gap-2 tw-pb-1 tw-text-sm tw-text-content-base">
            <ThreeStateCheckbox value={bulkDontRead} onChange={setBulkDontRead} />
            {T('dontReadActivities', 'Nie czytaj aktywności')}
          </label>
          <button
            type="button"
            onClick={applyBulk}
            disabled={!isSelectedAny}
            title={
              isSelectedAny
                ? T('applyToSelected', 'Zastosuj do zaznaczonych')
                : T('selectDiscsFirst', 'Zaznacz tarczki, aby zastosować')
            }
            className="tw-rounded tw-border tw-border-primary tw-bg-primary tw-px-3 tw-py-1.5 tw-text-sm tw-text-white hover:tw-bg-primary/90 disabled:tw-opacity-40"
          >
            {T('applyToSelected', 'Zastosuj do zaznaczonych')}
          </button>
        </div>

        {/* Per-row editing table — this is the internal scroll region so the
            bulk bar above and the modal footer below stay pinned for long lists. */}
        <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto">
          <table className="tw-w-full tw-table-auto tw-border-collapse">
            <TableHeader
              columns={columns}
              fontSize="md"
              isSelectedAny={isSelectedAny}
              sortConfig={null}
              toggleAll={toggleAll}
            />
            <tbody>
              {rows.map((row, idx) => {
                const isChecked = selectedIds.includes(row.discId)
                // Number discs within their own file, like WPF numbers discs per image.
                const discNo = rows.filter((r, i) => i <= idx && r.scanId === row.scanId).length
                return (
                  <tr
                    key={row.discId}
                    className={`tw-border-b tw-bg-white ${
                      isChecked ? 'tw-bg-primary/10' : ''
                    }`}
                  >
                    <TableCheckbox
                      disabled={false}
                      checked={isChecked}
                      onChange={() => toggleId(row.discId)}
                    />
                    {isMultiFile && (
                      <TableTextRow pointer={false}>{row.fileName}</TableTextRow>
                    )}
                    <TableTextRow pointer={false}>
                      {fmt(T('discNo', 'Tarczka {0}'), discNo)}
                    </TableTextRow>
                    <td className="tw-px-3 tw-py-2 tw-min-w-[180px]">
                      <DateField
                        value={row.chartDate}
                        setValue={(v) => updateRow(row.discId, { chartDate: v ?? '' })}
                        error={row.chartDate ? null : T('dayRequired', 'Podaj dzień')}
                      />
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-min-w-[220px]">
                      <SearchSelect
                        label=""
                        placeholder={T('searchDriver', 'Wyszukaj kierowcę...')}
                        value={row.driverId}
                        onChange={(id) => updateRow(row.discId, { driverId: id })}
                        useSearch={driverSearch}
                      />
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-min-w-[220px]">
                      <SearchSelect
                        label=""
                        placeholder={T('searchVehicle', 'Wyszukaj pojazd...')}
                        value={row.vehicleId}
                        onChange={(id) => updateRow(row.discId, { vehicleId: id })}
                        useSearch={vehicleSearch}
                      />
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-text-center">
                      <input
                        type="checkbox"
                        checked={row.dontReadActivities}
                        onChange={(e) =>
                          updateRow(row.discId, { dontReadActivities: e.target.checked })
                        }
                      />
                    </td>
                    {onDiscardDisc && (
                      <td className="tw-px-3 tw-py-2 tw-text-center">
                        <button
                          type="button"
                          onClick={() => setPendingDiscard({ row, discNo })}
                          disabled={discardingId !== null}
                          title={T(
                            'discardDiscHint',
                            'Odrzuć tarczkę — nie zostanie zaimportowana',
                          )}
                          aria-label={T('discardDisc', 'Odrzuć tarczkę')}
                          className="tw-rounded tw-p-1.5 tw-text-error hover:tw-bg-red-50 disabled:tw-opacity-40"
                        >
                          <FaIcon icon={faTrash} />
                        </button>
                      </td>
                    )}
                  </tr>
                )
              })}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={columns.length + 1}
                    className="tw-px-3 tw-py-8 tw-text-center tw-text-sm tw-text-neutral"
                  >
                    {T('allDiscsDiscarded', 'Wszystkie tarczki zostały odrzucone.')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <footer className="tw-mt-auto tw-flex tw-items-center tw-justify-end tw-gap-3 tw-border-t tw-border-base-300 tw-px-5 tw-py-3">
        {discardError && (
          <p className="tw-mr-auto tw-text-xs tw-text-error">{discardError}</p>
        )}
        {!discardError && rowsMissingDate > 0 && (
          <p className="tw-mr-auto tw-text-xs tw-text-error">
            {T('fillDayForEveryDisc', 'Uzupełnij dzień dla każdej tarczki.')}
          </p>
        )}
        <button
          type="button"
          onClick={onCancel}
          className="tw-rounded tw-border tw-border-slate-300 tw-px-3 tw-py-1.5 tw-text-sm"
        >
          {T('cancel', 'Anuluj')}
        </button>
        <button
          type="button"
          disabled={isSaving || rowsMissingDate > 0}
          onClick={handleContinue}
          title={
            rowsMissingDate > 0
              ? T('fillDayForEveryDisc', 'Uzupełnij dzień dla każdej tarczki.')
              : ''
          }
          className="tw-rounded tw-bg-blue-600 tw-px-3 tw-py-1.5 tw-text-sm tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-50"
        >
          {isSaving
            ? T('saving', 'Zapisywanie...')
            : rows.length === 0
              // Nothing left to import — the step can only be closed now.
              ? T('finish', 'Zakończ')
              : continueLabel ?? T('next', 'Dalej →')}
        </button>
      </footer>

      {/* Deleting a detected disc is irreversible, so it goes through the app's
          standard confirm dialog (portalled above this popup) instead of the
          browser's confirm box. */}
      <ConfirmModal
        isOpen={!!pendingDiscard}
        title={T('discardDisc', 'Odrzuć tarczkę')}
        text={T('confirmDiscardDisc', 'Odrzucić tę tarczkę? Nie zostanie zaimportowana.')}
        content={
          pendingDiscard
            ? [
                isMultiFile
                  ? `${fmt(T('discNo', 'Tarczka {0}'), pendingDiscard.discNo)} · ${
                      pendingDiscard.row.fileName
                    }`
                  : fmt(T('discNo', 'Tarczka {0}'), pendingDiscard.discNo),
              ]
            : undefined
        }
        actionText={T('discardConfirm', 'Odrzuć')}
        cancelText={T('cancel', 'Anuluj')}
        onClose={() => setPendingDiscard(null)}
        onConfirm={() => {
          const row = pendingDiscard?.row
          setPendingDiscard(null)
          if (row) void handleDiscard(row)
        }}
      />
    </div>
  )
}

export const StepAssign: React.FC<Props> = (props) => (
  <TableProvider>
    <StepAssignInner {...props} />
  </TableProvider>
)
